import { createEmptyCanvasPage } from "./canvasPageUtils";
import { persistCanvasPages } from "../../SectionElements/plugins/utils";
import { saveCanvasElementsToIndexedDB } from "./indexedDBStorageUtils";

/**
 * Updates canvas state and persists it to both IndexedDB and localStorage
 * @param {Object} params - Parameters object
 * @param {Object} params.canvasData - Canvas data to save
 * @param {string} params.snapshot - Canvas snapshot image data
 * @param {Function} params.setCanvasPages - State setter for canvas pages
 * @param {Function} params.setHasUnsavedChanges - State setter for unsaved changes flag
 * @param {string} params.pageId - Current page ID
 * @param {boolean} params.fromCanvasTemplate - Whether this is from a canvas template
 * @param {Array} params.texts - Text elements array
 * @param {Array} params.rectangles - Rectangle elements array
 * @param {Array} params.circles - Circle elements array
 * @param {Array} params.arrows - Arrow elements array
 * @param {Array} params.lines - Line elements array
 * @param {Array} params.doubleSidedArrows - Double-sided arrow elements array
 * @param {Array} params.scribbles - Scribble elements array
 */
export const updateCanvasState = async ({
  canvasData,
  snapshot,
  setCanvasPages,
  setHasUnsavedChanges,
  pageId,
  fromCanvasTemplate,
  texts,
  rectangles,
  circles,
  arrows,
  lines,
  doubleSidedArrows,
  scribbles,
}) => {
  // Update canvasPages state
  setCanvasPages((prevPages) => {
    if (!pageId || !prevPages) {
      return prevPages || [];
    }

    const updated = prevPages.map((p) => {
      if (!p?.id) {
        return p;
      }

      return p.id === pageId
        ? {
            ...p,
            snapshot,
            elements: canvasData,
            ...canvasData, // Spread individual arrays for backward compatibility
            lastModified: new Date().toISOString(),
          }
        : p;
    });

    const nonEmpty = updated.length > 0 ? updated : [createEmptyCanvasPage()];

    // Persist canvas pages metadata
    // NOTE: When coming from Canvas Template, we still need to persist the snapshot
    // but we can skip persisting full element arrays to avoid localStorage quota issues
    try {
      const pagesToPersist = nonEmpty.map(
        ({ id, snapshot, lastModified, canvasHeight: pageCanvasHeight }) => ({
          id,
          snapshot,
          lastModified,
          // CRITICAL FIX: Preserve each page's own canvasHeight
          canvasHeight: pageCanvasHeight ?? 100,
        })
      );

      // Always persist snapshot to localStorage for proper preview
      if (snapshot && pageId) {
        localStorage.setItem("canvasSnapshot", snapshot);
      }

      // Persist to canvasPages in localStorage
      if (!fromCanvasTemplate) {
        persistCanvasPages(pagesToPersist);
      }
    } catch (e) {
      console.error("Error persisting canvas pages:", e);
    }

    return nonEmpty;
  });

  // Save elements to IndexedDB
  await saveCanvasElementsToIndexedDB({
    data: canvasData,
    pageId: pageId,
  });

  // Update session storage
  const stateInfo = {
    hasChanges: true,
    lastModified: new Date().toISOString(),
    snapshot,
    pageId: pageId,
    elementCounts: {
      texts: texts?.length || 0,
      images: canvasData.images?.length || 0,
      rectangles: rectangles?.length || 0,
      circles: circles?.length || 0,
      arrows: arrows?.length || 0,
      lines: lines?.length || 0,
      doubleSidedArrows: doubleSidedArrows?.length || 0,
      scribbles: scribbles?.length || 0,
    },
  };

  sessionStorage.setItem("canvasStateInfo", JSON.stringify(stateInfo));
  setHasUnsavedChanges(false);
};
